import { CIError, ErrorSource, GraphQLErrorCode } from 'common/error/error_util';
import { CILogger } from 'common/log/log.provider';
import Sentry from 'common/sentry/sentry';

const generateErrorId = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const suffix = Math.random().toString(16).slice(2, 6).toUpperCase().padStart(4, '0');

    return `ERR-${year}-${month}${day}-${suffix}`;
};

// Correlates the fallback's id with the onError reporting call, without mutating the (possibly frozen) error.
const errorIdsByError = new WeakMap<object, string>();

export const getOrAssignErrorId = (error: unknown): string => {
    if (error && typeof error === 'object') {
        const existing = errorIdsByError.get(error);
        if (existing) {
            return existing;
        }
        const id = generateErrorId();
        errorIdsByError.set(error, id);

        return id;
    }

    return generateErrorId();
};

// Router errors are often {status, statusText}, not a real Error - avoids String(error)'s "[object Object]".
const describeNonError = (error: unknown): string => {
    if (error && typeof error === 'object') {
        const { status, statusText } = error as { status?: unknown; statusText?: unknown };
        if (status !== undefined || statusText !== undefined) {
            return `Router error response: ${[status, statusText].filter(Boolean).join(' ')}`;
        }
    }
    return String(error);
};

// Shared by both the render-time ErrorBoundary (has a componentStack) and the router's errorElement (doesn't).
export const reportBoundaryError = (error: unknown, componentStack?: string): void => {
    // Read/assign against the ORIGINAL value, matching ErrorBoundaryFallback, so the id stays the same one.
    const errorId = getOrAssignErrorId(error);
    const normalizedError = error instanceof Error ? error : new Error(describeNonError(error));

    const ciError = new CIError(
        normalizedError.message,
        GraphQLErrorCode.SYSTEM_ERROR,
        ErrorSource.UI,
        normalizedError
    );

    CILogger.getInstance().error('ErrorBoundary caught a render error', ciError, { errorId, componentStack });

    Sentry.captureException(normalizedError, { extra: { errorId, componentStack } });
};
